import React, { useContext, useState } from "react";

import { AlertContext } from "context/providers";
import { toSafeHttpUrl } from "components/UserCardList/helpers/userCard";
import {
  fetchFreshAssessmentReportLink,
  isAssessmentReportLinkExpired,
} from "utils/helpers/assessments";

/**
 * Anchor for opening an assessment's report link. Ropes report links expire
 * ~90 days after the assessment, so instead of blindly opening a dead link,
 * clicking checks for expiry first - if the link expired, the user is asked
 * whether to refresh it, and only then is a fresh link fetched (through the
 * getAssessmentReportLink query) and opened.
 */
const AssessmentReportLink = ({
  assessment,
  userId,
  className,
  title,
  children,
}) => {
  const { addGraphQLAlert } = useContext(AlertContext);
  const [freshLink, setFreshLink] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const href = toSafeHttpUrl(freshLink || assessment?.reportLink);

  const handleClick = async (event) => {
    // No stored link to guard, already refreshed, or the link is still
    // valid - let the anchor navigate as usual.
    if (
      !assessment?.reportLink ||
      freshLink ||
      !isAssessmentReportLinkExpired(assessment)
    ) {
      return;
    }

    event.preventDefault();

    if (isRefreshing) {
      return;
    }

    // Without an interviewId the backend cannot refresh the link - fall back
    // to opening the stored one as-is (only if the scheme is safe).
    if (!assessment.interviewId) {
      const safeStoredLink = toSafeHttpUrl(assessment.reportLink);

      if (safeStoredLink) {
        window.open(safeStoredLink, "_blank", "noreferrer");
      }

      return;
    }

    const shouldRefresh = window.confirm(
      "The report link for this assessment has expired. Would you like to fetch a new link and open it?"
    );

    if (!shouldRefresh) {
      return;
    }

    // Open the tab synchronously within the click gesture so popup blockers
    // do not get in the way, then point it at the refreshed link.
    const reportTab = window.open("", "_blank");

    if (reportTab) {
      reportTab.opener = null;
    }

    try {
      setIsRefreshing(true);

      const link = await fetchFreshAssessmentReportLink(assessment, userId);
      const safeLink = toSafeHttpUrl(link);

      if (!safeLink) {
        throw new Error("Could not refresh the assessment report link");
      }

      setFreshLink(safeLink);

      if (reportTab) {
        reportTab.location = safeLink;
      }
    } catch (err) {
      console.log(
        "Something went wrong refreshing the assessment report link",
        err
      );
      addGraphQLAlert(err);
      reportTab?.close();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!href) {
    return (
      <span className={className} title={title}>
        {children}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={className}
      title={title}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export default AssessmentReportLink;
